import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardMedia, 
  Button, 
  Chip, 
  CircularProgress, 
  Alert,
  Divider,
  Paper,
  IconButton,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  useTheme,
} from '@mui/material';
import {
  Add,
  Remove,
  ShoppingCart,
  LocalShipping,
  Verified,
  Warning,
  Info,
  CheckCircle,
  Error,
  ExpandMore,
  Favorite,
  Share,
  Security,
  LocalPharmacy,
  
  ArrowBack,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { CiImageOff } from 'react-icons/ci';
import userService from '../services/userService';
import useUserStore from '../stores/userStore';
import RelatedProducts from '../components/RelatedProducts';
import useCartGuard from '../hooks/useCartGuard';

const ViewOTC = () => {
  const { id } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const { addOtcToCart, fetchCartCount, fetchCart } = useUserStore();
  const { requireAuth } = useCartGuard();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);
  const [quantityInCart, setQuantityInCart] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [cartLoading, setCartLoading] = useState(false);
  const [isInCart, setIsInCart] = useState(false);

  useEffect(() => {
    if (id) {
      loadProductDetails();
    }
  }, [id]);

  const loadProductDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading product details for ID:', id);
      const result = await userService.getOtcByID(id);
      console.log('Product details API response:', result);
      console.log('OTC Product images field:', result.data?.images);
      
      if (result.data) {
        setProduct(result.data);
        // Check if product is in cart
        await checkCartQuantity(result.data.id);
      } else {
        setError('Product not found');
      }
    } catch (err) {
      console.error('Error loading product details:', err);
      setError('Failed to load product details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const checkCartQuantity = async (productId) => {
    try {
      const cartData = await fetchCart();
      if (cartData && cartData.cart && cartData.cart.cart_items) {
        const cartItem = cartData.cart.cart_items.find(item => 
          item.otc && item.otc.id === productId
        );
        const quantity = cartItem ? cartItem.quantity : 0;
        setQuantityInCart(quantity);
        setIsInCart(quantity > 0);
      }
    } catch (err) {
      console.error('Error checking cart quantity:', err);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAddToCart = async () => {
    if (!id) return;
    if (!requireAuth()) return;
    
    setCartLoading(true);
    try {
      await addOtcToCart(product.id);
      // Re-check cart quantity to get accurate count
      await checkCartQuantity(product.id);
      await fetchCartCount();
      showSnackbar('Product added to cart successfully!', 'success');
    } catch (err) {
      console.error('Error adding to cart:', err);
      showSnackbar('Failed to add product to cart', 'error');
    } finally {
      setCartLoading(false);
    }
  };

  const handleRemoveFromCart = async () => {
    if (!id) return;
    if (!requireAuth()) return;
    
    setCartLoading(true);
    try {
      await userService.removeOCart(product.id);
      // Re-check cart quantity to get accurate count
      await checkCartQuantity(product.id);
      await fetchCartCount();
      showSnackbar('Product removed from cart', 'info');
    } catch (err) {
      console.error('Error removing from cart:', err);
      showSnackbar('Failed to remove product from cart', 'error');
    } finally {
      setCartLoading(false);
    }
  };

  const handleQuantityChange = async (action) => {
    if (!id) return;
    if (!requireAuth()) return;
    
    setCartLoading(true);
    try {
      if (action === 'inc') {
        await addOtcToCart(product.id);
      } else if (action === 'dec') {
        await userService.removeOCart(product.id);
      }
      
      // Re-check cart quantity to get accurate count
      await checkCartQuantity(product.id);
      await fetchCartCount();
      
      showSnackbar(
        action === 'inc' ? 'Item added to cart!' : 'Item removed from cart!', 
        'success'
      );
    } catch (err) {
      console.error('Error updating cart:', err);
      showSnackbar('Failed to update cart', 'error');
    } finally {
      setCartLoading(false);
    }
  };

  const getProductImages = (images) => {
    if (!images) {
      return [];
    }
    
    // Handle both array and string formats
    let imagePaths;
    if (Array.isArray(images)) {
      // If images is an array, return the URLs directly
      imagePaths = images;
    } else {
      // If images is a string, split and construct paths to local assets
      const imageNames = images.split(',').map(img => img.trim()).filter(img => img);
      imagePaths = imageNames.map(img => `/assets/images_1mg_otc/${img}`);
    }
    
    // Debug logging
    console.log('OTC Image Debug:', {
      originalImages: images,
      imagePaths: imagePaths
    });
    
    return imagePaths;
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2, alignSelf: 'center' }}>
            Loading product details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBack />}
          onClick={handleBackClick}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6" color="text.secondary">
          Product not found
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBack />}
          onClick={handleBackClick}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 , }}>
        <Grid container spacing={4}>
          {/* Product Image */}
          <Grid item xs={12} md={6} sx={{maxWidth: '400px'}}>
            <Card>
              {getProductImages(product.images).length > 0 ? (
                <>
                  <CardMedia
                    component="img"
                    height="400"
                    image={getProductImages(product.images)[currentImageIndex]}
                    alt={product.name}
                    sx={{ objectFit: 'contain', p: 2, width: '100%' }}
                  />
                  {getProductImages(product.images).length > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, p: 2 }}>
                      {getProductImages(product.images).map((img, index) => (
                        <CardMedia
                          key={index}
                          component="img"
                          height="60"
                          width="60"
                          image={img}
                          alt={`${product.name} ${index + 1}`}
                          sx={{ 
                            objectFit: 'contain', 
                            cursor: 'pointer',
                            border: currentImageIndex === index ? 2 : 1,
                            borderColor: currentImageIndex === index ? 'primary.main' : 'divider',
                            borderRadius: 1,
                            opacity: currentImageIndex === index ? 1 : 0.7,
                            minWidth: '60px',
                            maxWidth: '60px'
                          }}
                          onClick={() => setCurrentImageIndex(index)}
                        />
                      ))}
                    </Box>
                  )}
                </>
              ) : (
                <Box
                  sx={{
                    height: 400,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 2,
                    bgcolor: 'grey.50'
                  }}
                >
                  <CiImageOff size={350} color={theme.palette.primary.main} />
                </Box>
              )}
            </Card>
          </Grid>

          {/* Product Details */}
          <Grid item xs={12} md={6} sx={{maxWidth: '650px'}}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" component="h1" gutterBottom>
                {product.name}
              </Typography>
              
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {product.manufacturer}
              </Typography>

              {/* Product Details Card */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Verified color="success" />
                  Product Details
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Category:</Typography>
                    <Typography variant="body2">{product.category || 'Not specified'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Sub-category:</Typography>
                    <Typography variant="body2">{product.sub_category || 'Not specified'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Packaging:</Typography>
                    <Typography variant="body2">{product.pack_info || product.packaging || 'Not specified'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Country of Origin:</Typography>
                    <Typography variant="body2">{product.otc_info?.country_of_origin || 'Not specified'}</Typography>
                  </Box>
                </Box>
              </Paper>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h4" color="primary">
                    ₹{product.best_price || product.price}
                  </Typography>
                  {product.best_price && product.best_price !== product.price && (
                    <>
                      <Typography variant="h6" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                        ₹{product.price}
                      </Typography>
                      <Chip
                        label={`Save ₹${(parseFloat(product.price) - parseFloat(product.best_price)).toFixed(2)}`}
                        color="success"
                        size="small"
                      />
                    </>
                  )}
                </Box>
                {product.prescription_required && (
                  <Chip
                    icon={<Warning />}
                    label="Prescription Required"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Quantity Selector */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Quantity in Cart
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <IconButton
                      onClick={() => handleQuantityChange('dec')}
                      disabled={cartLoading || quantityInCart <= 0}
                    >
                      <Remove />
                    </IconButton>
                    <Typography variant="h6" sx={{ minWidth: 40, textAlign: 'center' }}>
                      {quantityInCart}
                    </Typography>
                    <IconButton 
                      onClick={() => handleQuantityChange('inc')}
                      disabled={cartLoading}
                    >
                      <Add />
                    </IconButton>
                  </Box>
                </Box>
              </Box>

              {/* Cart Actions */}
              <Stack spacing={2} sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<Favorite />}
                    sx={{ flex: 1 }}
                  >
                    Wishlist
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Share />}
                    sx={{ flex: 1 }}
                  >
                    Share
                  </Button>
                </Stack>
              </Stack>

              {/* Product Info */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Product Information
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Verified color="success" />
                  <Typography variant="body2">Genuine Product</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LocalShipping color="primary" />
                  <Typography variant="body2">Free delivery on orders above ₹500</Typography>
                </Box>
                {product.otc_info?.use_of && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Info color="info" />
                    <Typography variant="body2">
                      <strong>Use:</strong> {product.otc_info.use_of}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          </Grid>
        </Grid>

        {/* Product Information Accordions */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            Product Information
          </Typography>
          
          <Grid container spacing={2}>
            {/* Introduction & Description */}
            <Grid item xs={12} sx={{ width: '100%' }}>
              <Accordion defaultExpanded sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <AccordionSummary 
                  expandIcon={<ExpandMore />} 
                  sx={{ 
                    width: '100%',
                    '& .MuiAccordionSummary-content': {
                      flex: 1,
                      minWidth: 0,
                      width: '100%'
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, width: '100%', minWidth: 0 }}>
                    <Info color="primary" />
                    Introduction & Description
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" paragraph>
                    {product.otc_info?.introduction || ''}
                  </Typography>
                  {product.otc_info?.description && (
                    <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line' }}>
                      {product.otc_info.description}
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* How to Use & Benefits */}
            <Grid item xs={12} sx={{ width: '100%' }}>
              <Accordion sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <AccordionSummary 
                  expandIcon={<ExpandMore />} 
                  sx={{ 
                    width: '100%',
                    '& .MuiAccordionSummary-content': {
                      flex: 1,
                      minWidth: 0,
                      width: '100%'
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, width: '100%', minWidth: 0 }}>
                    <LocalPharmacy color="primary" />
                    How to Use & Benefits
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>How to Use:</Typography>
                    <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line' }}>
                      {product.otc_info?.how_to_use || 'Please follow the instructions on the label.'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" gutterBottom>Benefits:</Typography>
                    <Typography variant="body1" paragraph>
                      {product.otc_info?.benefits || 'Please consult your doctor for information about benefits.'}
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Use of Product */}
            <Grid item xs={12} sx={{ width: '100%' }}>
              <Accordion sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <AccordionSummary 
                  expandIcon={<ExpandMore />} 
                  sx={{ 
                    width: '100%',
                    '& .MuiAccordionSummary-content': {
                      flex: 1,
                      minWidth: 0,
                      width: '100%'
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, width: '100%', minWidth: 0 }}>
                    <Verified color="success" />
                    Product Use
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" paragraph>
                    {product.otc_info?.use_of || 'Product use information not available.'}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Safety Advice */}
            <Grid item xs={12} sx={{ width: '100%' }}>
              <Accordion sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <AccordionSummary 
                  expandIcon={<ExpandMore />} 
                  sx={{ 
                    width: '100%',
                    '& .MuiAccordionSummary-content': {
                      flex: 1,
                      minWidth: 0,
                      width: '100%'
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, width: '100%', minWidth: 0 }}>
                    <Security color="primary" />
                    Safety Advice
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" paragraph>
                    {product.otc_info?.safety_advise || 'Please consult your doctor for safety information.'}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Storage & Ingredients */}
            <Grid item xs={12} sx={{ width: '100%' }}>
              <Accordion sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <AccordionSummary 
                  expandIcon={<ExpandMore />} 
                  sx={{ 
                    width: '100%',
                    '& .MuiAccordionSummary-content': {
                      flex: 1,
                      minWidth: 0,
                      width: '100%'
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, width: '100%', minWidth: 0 }}>
                    <LocalShipping color="primary" />
                    Storage & Ingredients
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>Storage Instructions:</Typography>
                    <Typography variant="body1" paragraph>
                      {product.otc_info?.storage || 'Store in a cool, dry place away from direct sunlight.'}
                    </Typography>
                  </Box>
                  {product.otc_info?.ingredients && (
                    <Box>
                      <Typography variant="h6" gutterBottom>Ingredients:</Typography>
                      <Typography variant="body1" paragraph>
                        {product.otc_info.ingredients}
                      </Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Manufacturer Information */}
            <Grid item xs={12} sx={{ width: '100%' }}>
              <Accordion sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <AccordionSummary 
                  expandIcon={<ExpandMore />} 
                  sx={{ 
                    width: '100%',
                    '& .MuiAccordionSummary-content': {
                      flex: 1,
                      minWidth: 0,
                      width: '100%'
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, width: '100%', minWidth: 0 }}>
                    <Verified color="success" />
                    Manufacturer Details
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>Manufacturer:</Typography>
                    <Typography variant="body1" paragraph>
                      {product.manufacturer || 'Manufacturer information not available.'}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>Country of Origin:</Typography>
                    <Typography variant="body1" paragraph>
                      {product.otc_info?.country_of_origin || 'Not specified'}
                    </Typography>
                  </Box>
                  {product.otc_info?.manufacturer_address && (
                    <Box>
                      <Typography variant="h6" gutterBottom>Manufacturer Address:</Typography>
                      <Typography variant="body1" paragraph>
                        {product.otc_info.manufacturer_address}
                      </Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </Box>

        {/* Related Products Section */}
        <RelatedProducts 
          productId={id} 
          productType="otc"
          title="Similar Products"
        />
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ViewOTC;
